import supabase from "@/services/supabaseClient";

export const DOCTOR_PAYMENT_METHOD_OPTIONS = [
  {
    id: "card",
    label: "Credit • Debit Card",
    helper: "Pay securely with a local or international card.",
    icon: "/icons/doctor/credit_card.svg",
    metaKey: "paystack_card",
  },
  {
    id: "instant_eft",
    label: "Instant EFT",
    icon: "/icons/doctor/account_balance.svg",
    helper: "Pay securely through your bank.",
    metaKey: "paystack_instant_eft",
  },
  {
    id: "capitec_pay",
    label: "Capitec Pay",
    // Keep the compact bank symbol at row size. Capability and runtime
    // verification, rather than this preference, determine availability.
    icon: "/icons/Capitec_Bank_Symbol_0.svg",
    helper: "Approve securely in your Capitec banking app.",
    metaKey: "paystack_capitec_pay",
  },
  {
    id: "qr",
    label: "Scan to Pay",
    // Match the established Prescription action icon beside Print/Download.
    icon: "/icons/appointments/e-prescription/qr-code-filled.svg",
    helper: "Complete payment securely with the available QR payment route.",
    metaKey: "paystack_qr",
  },
  {
    id: "apple_pay",
    label: "Apple Pay",
    icon: "/icons/apple-icon.svg",
    helper: "Pay securely with Apple Pay on an eligible device.",
    metaKey: "paystack_apple_pay",
  },
  {
    id: "google_pay",
    label: "Google Pay",
    // The supplied asset is the standalone Google mark, not a wordmark.
    icon: "/assets/images/google.svg",
    helper: "Pay securely with Google Pay on an eligible device.",
    metaKey: "paystack_google_pay",
  },
  {
    id: "samsung_pay",
    label: "Samsung Pay",
    icon: "/icons/samsung-pay.svg",
    // The supplied artwork includes a generous canvas around the mark. Keep
    // the shared 24px icon slot, but optically match the other row icons.
    iconClassName: "scale-[1.5]",
    helper: "Pay securely with Samsung Pay on an eligible device.",
    metaKey: "paystack_samsung_pay",
  },
  {
    id: "mtn_momo",
    label: "MTN MoMo",
    icon: "/icons/mtnmomo-logo-square-rounded.svg",
    helper: "Accept secure payments through MTN Mobile Money.",
    metaKey: "mtn_momo",
  },
];

const DEFAULT_PAYMENT_METHOD_IDS = ["card"];
export const DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS = DOCTOR_PAYMENT_METHOD_OPTIONS.filter(
  (option) => option.active !== false,
);

/**
 * Booking screens use option ids; clinician payout metadata uses metaKey.
 * Accept either at a route boundary, but always pass the booking id forward.
 */
export function normalizeDoctorPaymentMethodId(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  // Legacy doctor configuration represented a single provider-backed card
  // route as separate credit/debit flags. Preserve historic bookings while
  // converging new configuration on one Paystack Card method. Bank transfer
  // and wallet never represented a verified ZA hosted Paystack channel, so
  // they intentionally have no execution-route migration.
  if (["credit", "debit", "credit_cards", "debit_cards"].includes(raw)) return "card";
  if (["eft", "instant_eft"].includes(raw)) return "instant_eft";
  const option = DOCTOR_PAYMENT_METHOD_OPTIONS.find(
    (entry) => entry.id === raw || entry.metaKey === raw,
  );
  return option?.id || "";
}

export function parseDoctorPayoutMeta(metaRaw) {
  if (!metaRaw) return {};
  if (typeof metaRaw === "object") return metaRaw;
  const text = String(metaRaw || "").trim();
  if (!text) return {};
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function preferenceEnabled(meta, option) {
  if (typeof meta?.[option.metaKey] === "boolean") return meta[option.metaKey];
  if (option.id === "card") return Boolean(meta?.credit_cards || meta?.debit_cards);
  if (option.id === "instant_eft") return Boolean(meta?.instant_eft);
  if (option.id === "mtn_momo") return Boolean(meta?.mtn_momo);
  return false;
}

export function getSelectedDoctorPaymentMethods(
  metaRaw,
  allowedMethodIds = DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS.map((option) => option.id),
) {
  const meta = parseDoctorPayoutMeta(metaRaw);
  const allowedIds = new Set(
    (Array.isArray(allowedMethodIds) ? allowedMethodIds : [])
      .map(normalizeDoctorPaymentMethodId)
      .filter(Boolean),
  );
  return DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS.filter(
    (option) => allowedIds.has(option.id) && preferenceEnabled(meta, option),
  );
}

export function summarizeSelectedDoctorPaymentMethods(metaRaw, allowedMethodIds) {
  const selected = getSelectedDoctorPaymentMethods(metaRaw, allowedMethodIds);
  return {
    count: selected.length,
    labels: selected.map((item) => item.label),
    methods: selected,
  };
}

export function getEnabledDoctorPaymentMethods(metaRaw, providerMethodIds = DEFAULT_PAYMENT_METHOD_IDS) {
  const providerIds = new Set(
    (Array.isArray(providerMethodIds) ? providerMethodIds : DEFAULT_PAYMENT_METHOD_IDS)
      .map(normalizeDoctorPaymentMethodId)
      .filter(Boolean),
  );
  const enabled = getSelectedDoctorPaymentMethods(metaRaw, [...providerIds]);
  if (enabled.length > 0) return enabled;
  const saved = parseDoctorPayoutMeta(metaRaw);
  if (DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS.some(option => Object.prototype.hasOwnProperty.call(saved, option.metaKey))) return [];
  // Existing clinicians with no saved collection preference retain the
  // universally available Card default; a clinician setting cannot create a
  // provider capability that the active environment has not confirmed.
  return DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS.filter(
    (option) => DEFAULT_PAYMENT_METHOD_IDS.includes(option.id) && providerIds.has(option.id),
  );
}

export function summarizeDoctorPaymentMethods(metaRaw, providerMethodIds) {
  const enabled = getEnabledDoctorPaymentMethods(metaRaw, providerMethodIds);
  return {
    count: enabled.length,
    labels: enabled.map((item) => item.label),
    methods: enabled,
  };
}

export async function loadDoctorPaymentMethods(doctorUserId, providerMethodIds) {
  if (!doctorUserId) {
    return {
      methods: getEnabledDoctorPaymentMethods(null, providerMethodIds),
      meta: {},
    };
  }

  const { data, error } = await supabase
    .from("doctor_profiles")
    .select("payout_meta")
    .eq("user_id", doctorUserId)
    .maybeSingle();

  if (error) throw error;
  const meta = parseDoctorPayoutMeta(data?.payout_meta);
  return {
    methods: getEnabledDoctorPaymentMethods(meta, providerMethodIds),
    meta,
  };
}

export function formatDoctorPaymentMethod(option, t = (_, fallback) => fallback) {
  return t(`settings.payoutPreferences.methods.${option.id}`, option.label);
}

// Settings display saved preference truth independently of executable provider capability.
// Booking still uses getEnabledDoctorPaymentMethods and fails closed on unavailable rails.
export function projectSavedPaymentMethodOptions(saved = {}, edited = {}, available = []) {
  // Payment setup is a preference surface: show every supported AfriMed row,
  // while the capability authority keeps unproven rails disabled. Hiding a
  // row just because its provider is unavailable makes saved preferences
  // ambiguous and encourages a false inference from a checkbox alone.
  return DOCTOR_ACTIVE_PAYMENT_METHOD_OPTIONS;
}
